import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

const CancelOrderDialog = ({ order, onConfirm, onCancel }) => {
    const [reason, setReason] = useState("");
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [loading, setLoading] = useState(false);

    const commonReasons = [
        "Changed my mind",
        "Found better price elsewhere",
        "No longer needed",
        "Ordered by mistake",
        "Delivery taking too long",
        "Other",
    ];

    const handleFirstCancel = () => {
        if (!reason.trim()) {
            alert("Please select or enter a reason for cancellation");
            return;
        }
        setShowConfirmation(true);
    };

    const handleFinalConfirm = async () => {
        setLoading(true);
        try {
            await onConfirm(reason);
        } catch (error) {
            console.error("Error cancelling order:", error);
            alert("Failed to cancel order. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (!order) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50  flex items-center justify-center p-4 z-50"
                onClick={onCancel}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-xl max-w-md w-full"
                    onClick={e => e.stopPropagation()}
                >
                    {!showConfirmation ? (
                        // Step 1: Reason Selection
                        <>
                            <div className="p-6">
                                <div className="flex items-center mb-4">
                                    <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                                        <svg
                                            className="w-6 h-6 text-yellow-600"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"
                                            />
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900">Cancel Order</h2>
                                        <p className="text-gray-600">
                                            Order #{order.orderNumber || order._id.slice(-8)}
                                        </p>
                                    </div>
                                </div>

                                <div className="mb-6">
                                    <p className="text-gray-700 mb-4">
                                        Please let us know why you'd like to cancel this order:
                                    </p>

                                    <div className="space-y-2 mb-4">
                                        {commonReasons.map(reasonOption => (
                                            <label key={reasonOption} className="flex items-center">
                                                <input
                                                    type="radio"
                                                    name="cancelReason"
                                                    value={reasonOption}
                                                    checked={reason === reasonOption}
                                                    onChange={e => setReason(e.target.value)}
                                                    className="mr-3 text-red-600 focus:ring-red-500"
                                                />
                                                <span className="text-gray-700">{reasonOption}</span>
                                            </label>
                                        ))}
                                    </div>

                                    {reason === "Other" && (
                                        <textarea
                                            placeholder="Please specify your reason..."
                                            value={reason === "Other" ? "" : reason}
                                            onChange={e => setReason(e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                            rows="3"
                                        />
                                    )}
                                </div>

                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                                    <div className="flex">
                                        <svg
                                            className="w-5 h-5 text-yellow-600 mr-2 mt-0.5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>
                                        <div className="text-sm">
                                            <p className="text-yellow-800 font-medium">Important:</p>
                                            <p className="text-yellow-700 mt-1">
                                                Once cancelled, this order cannot be restored. If you've already paid, a
                                                refund will be processed within 5-7 business days.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50 px-6 py-4 rounded-b-xl">
                                <div className="flex space-x-3">
                                    <button
                                        onClick={onCancel}
                                        className="flex-1 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        Keep Order
                                    </button>
                                    <button
                                        onClick={handleFirstCancel}
                                        disabled={!reason.trim()}
                                        className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Continue
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        // Step 2: Final Confirmation
                        <>
                            <div className="p-6">
                                <div className="text-center mb-6">
                                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <svg
                                            className="w-8 h-8 text-red-600"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"
                                            />
                                        </svg>
                                    </div>
                                    <h2 className="text-xl font-bold text-gray-900 mb-2">Confirm Cancellation</h2>
                                    <p className="text-gray-600">
                                        Are you absolutely sure you want to cancel this order?
                                    </p>
                                </div>

                                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                                    <h3 className="font-medium text-red-900 mb-2">Order Details:</h3>
                                    <div className="text-sm text-red-800 space-y-1">
                                        <p>Order #{order.orderNumber || order._id.slice(-8)}</p>
                                        <p>Total Amount: ₹{order.pricing?.total || order.totalAmount || 0}</p>
                                        <p>Items: {order.items?.length || 0}</p>
                                        <p>Reason: {reason}</p>
                                    </div>
                                </div>

                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                                    <div className="flex items-start">
                                        <svg
                                            className="w-5 h-5 text-gray-600 mr-2 mt-0.5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>
                                        <div className="text-sm text-gray-700">
                                            <p className="font-medium">This action is irreversible!</p>
                                            <p className="mt-1">
                                                Your order will be permanently cancelled and cannot be restored. Any
                                                payments will be refunded according to our refund policy.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50 px-6 py-4 rounded-b-xl">
                                <div className="flex space-x-3">
                                    <button
                                        onClick={() => setShowConfirmation(false)}
                                        disabled={loading}
                                        className="flex-1 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                                    >
                                        Go Back
                                    </button>
                                    <button
                                        onClick={handleFinalConfirm}
                                        disabled={loading}
                                        className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                                    >
                                        {loading ? (
                                            <>
                                                <svg
                                                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <circle
                                                        className="opacity-25"
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="4"
                                                    ></circle>
                                                    <path
                                                        className="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                    ></path>
                                                </svg>
                                                Cancelling...
                                            </>
                                        ) : (
                                            "Yes, Cancel Order"
                                        )}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default CancelOrderDialog;
